import React, { useEffect, useState } from "react";
import {
  Filter,
  Download,
  RefreshCw,
  FileText,
  Users,
  Gavel,
  Clock,
  CheckCircle,
  AlertCircle,
  ChevronDown,
} from "lucide-react";

import {
  exportDailyListCSV,
  exportDailyListExcel,
  exportDailyListPDF,
  getAllCourtRooms,
  getDailyList,
} from "../../../api/reportAPI";
import logger from "../../../utils/logger";
import { formatDisplayDate, toISOForDB } from "../../../utils/Date";
import DateInput from "react-calendar-ui-kit";

interface RoomOption {
  _id?: string;
  courtRoomNo: string;
  courtName: string;
  courtType?: string;
}

interface CaseRow {
  sNo: number;
  _id: string;
  caseNumber?: string;
  cnr?: string;
  caseType?: string;
  caseStage?: string;
  courtRoomNo?: string;
  courtRoomName?: string;
  petitioner?: string;
  respondent?: string;
  filingDate?: string;
  hearingDate?: string;
  registrationNumber?: string;
  advocatePetitioner?: string;
  advocateRespondent?: string;
}

const DailyListReport: React.FC = () => {
  const [date, setDate] = useState<string>("");
  const [courtroom, setCourtroom] = useState<string>("");
  const [showTable, setShowTable] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [roomOptions, setRoomOptions] = useState<RoomOption[]>([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [cases, setCases] = useState<CaseRow[]>([]);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);


  // groupings
  const [miscellaneousData, setMiscellaneousData] = useState<CaseRow[]>([]);
  const [prosecutionEvidenceData, setProsecutionEvidenceData] = useState<
    CaseRow[]
  >([]);
  const [defendantEvidenceData, setDefendantEvidenceData] = useState<CaseRow[]>(
    []
  );
  const [appearancesData, setAppearancesData] = useState<CaseRow[]>([]);
  const [finalDecisionData, setFinalDecisionData] = useState<CaseRow[]>([]);
  const [argumentsData, setArgumentsData] = useState<CaseRow[]>([]);

  useEffect(() => {
    fetchCourtRooms();
  }, []);

  const fetchCourtRooms = async () => {
    try {
      const res = await getAllCourtRooms();
      if (res.data?.status) {
        const mapped = res.data.data.map((r: any) => ({
          _id: r._id,
          courtRoomNo: r.roomNumber || r.courtRoomNo,
          courtName: r.courtRoomName || r.courtName,
        }));
        setRoomOptions(mapped);
      } else {
        setRoomOptions([]);
      }
    } catch (err) {
      logger.error("fetchCourtRooms error:", err);
      setRoomOptions([]);
    }
  };

  const handleDateInputValidation = (
  value: string,
  setter: (v: string) => void,
) => {
  if (!value) return setter("");
  const isoValue = toISOForDB(value) ?? "";
  setter(isoValue);
};

  // const handleDateInputValidation = (value: string) => {
  //   const year = value.split("-")[0];
  //   if (year.length > 4) {
  //     setError("Year must be only 4 digits.");
  //     return;
  //   }
  //   setError("");
  //   setDate(value);
  // };

  const handleFilter = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!date || !courtroom) {
      setError("Please select both Date and Court Room Number");
      return;
    }
    setError("");
    setLoading(true);
   
    
    setShowTable(true);

    try {
      const res = await getDailyList({ courtRoomNo: courtroom, date });
      const allData = res.data?.data || [];
      const filterDate = date.split("T")[0]; 
      const filteredData = allData.filter((item: any) => {
        if (!item.hearingDate) return false;
        const formattedHearingDate = new Date(item.hearingDate)
          .toISOString()
          .split("T")[0];
           return formattedHearingDate === filterDate;
      });

      logger.log("Filtered Data:", filteredData);

      if (filteredData.length > 0) {
        setCases(filteredData);
        setError("");
      } else {
        setCases([]);
        setError("");
      }
    } catch (err) {
      logger.error("daily-list fetch error:", err);
      setCases([]);
      setError("Error fetching data");
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (cases.length > 0) {
      const evidenceCases = cases.filter(
        (item) => item.caseStage?.toLowerCase() === "evidence"
      );

      const argumentCases = cases.filter(
        (item) => item.caseStage?.toLowerCase() === "arguments"
      );

      const appearanceCases = cases.filter(
        (item) => item.caseStage?.toLowerCase() === "appearence"
      );

      const finalDecisionCases = cases.filter(
        (item) => item.caseStage?.toLowerCase() === "final decision"
      );

      const miscellaneousCases = cases.filter(
        (item) =>
          !["evidence", "arguments", "appearence", "final decision"].includes(
            item.caseStage?.toLowerCase() || ""
          )
      );

      setProsecutionEvidenceData(evidenceCases);
      setArgumentsData(argumentCases);
      setDefendantEvidenceData([]);
      setAppearancesData(appearanceCases);
      setFinalDecisionData(finalDecisionCases);
      setMiscellaneousData(miscellaneousCases);
    } else {
      // ✅ reset all states when no data found
      setMiscellaneousData([]);
      setProsecutionEvidenceData([]);
      setDefendantEvidenceData([]);
      setAppearancesData([]);
      setFinalDecisionData([]);
      setArgumentsData([]);
    }
  }, [cases]);

  const handleReset = async () => {
    try {
      setResetLoading(true)
      setDate("");
      setCourtroom("");
      setShowTable(false);
      setError("");
      setCases([]);
      setMiscellaneousData([]);
      setProsecutionEvidenceData([]);
      setDefendantEvidenceData([]);
      setAppearancesData([]);
      setFinalDecisionData([]);
    }
    catch (error) {
      logger.error("error", error)
    }
    finally {
      setResetLoading(false)
    }
  };

  const handleExport = async (type: "csv" | "excel" | "pdf") => {
    if (!date || !courtroom) {
      setError("Please select both Date and Court Room Number");
      return;
    }


    try {
      setExportLoading(true);

      setShowExportDropdown(false);
      const params = { courtRoomNo: courtroom, date };
      let res;
      if (type === "csv") res = await exportDailyListCSV(params);
      else if (type === "excel") res = await exportDailyListExcel(params);
      else res = await exportDailyListPDF(params);

      const contentType =
    res.headers["content-type"] != null
    ? String(res.headers["content-type"])
    : undefined;

     const blob = new Blob([res.data], { type: contentType });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        type === "csv"
          ? "DailyList.csv"
          : type === "excel"
            ? "DailyList.xlsx"
            : "DailyList.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      logger.error("export error:", err);
      setError("No data available to export");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="min-h-screen  max-w-screen p-4">
      {/* Summary Cards */}
      {showTable && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          {/* Total Cases */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Cases</p>
                <p className="text-3xl font-bold text-blue-600">
                  {cases.length}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          {/* Appearances */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Appearances</p>
                <p className="text-3xl font-bold text-green-600">
                  {appearancesData.length}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <Users className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          {/* Evidence */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Evidence Items
                </p>
                <p className="text-3xl font-bold text-orange-600">
                  {prosecutionEvidenceData.length}
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </div>

          {/* ✅ Arguments */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Arguments</p>
                <p className="text-3xl font-bold text-yellow-600">
                  {argumentsData.length}
                </p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Gavel className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>

          {/* Final Decisions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Decisions</p>
                <p className="text-3xl font-bold text-purple-600">
                  {finalDecisionData.length}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
        <div className="space-y-4">
          <div className="flex items-center space-x-2 mb-4">
            <Filter className="w-5 h-5 text-gray-500" />
            <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
          </div>

          <form onSubmit={handleFilter}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Date
                </label>
                <DateInput
    value={formatDisplayDate(date) ?? ""}
    onChange={(val: string) =>
      handleDateInputValidation(val, setDate)
    }
  />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Court Room Number
                </label>
                <div className="relative">
                  <select
                    value={courtroom}
                    onChange={(e) => setCourtroom(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors appearance-none"
                  >
                    <option value="">Select Courtroom</option>
                    {roomOptions.map((room) => (
                      <option key={room._id} value={room._id}>
                        {room.courtRoomNo} - {room.courtName}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              <div className="flex items-end space-y-2">
                <div className="flex flex-wrap gap-2 w-full">
                  <button
                    type="submit"
                    disabled={loading}
                    className=" w-42 inline-flex items-center px-6 py-2 bg-emerald-600 text-white font-medium rounded-lg disabled:opacity-50"
                  >
                    {loading ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Filter className="w-4 h-4 mr-2" />
                    )}
                    {loading ? "Applying..." : "Apply Filters"}
                  </button>


                  <button
                    type="button"
                    onClick={handleReset}
                    disabled={resetLoading}
                    className={`inline-flex items-center px-6 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors
                       ${resetLoading ? "opacity-50 cursor-not-allowed" : "opacity-100 cursor-pointer"}`}
                  >
                    <RefreshCw
                      className={`w-4 h-4 mr-2 ${resetLoading ? "animate-spin" : ""}`}
                    />
                    Reset
                  </button>
                </div>
              </div>
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {showTable && (
              <div className="flex flex-wrap mt-3 items-center md:justify-end gap-4 pt-4">
                <div className="relative">
                  <button
                    type="button"
                    disabled={exportLoading}
                    onClick={() => setShowExportDropdown(!showExportDropdown)}
                    className="w-48 inline-flex items-center justify-center gap-2 px-6 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {exportLoading ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    <span className="whitespace-nowrap">
                      {exportLoading ? "Exporting..." : "Export Report"}
                    </span>
                  </button>


                  {showExportDropdown && (
                    <div className=" absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                      <button
                        onClick={() => handleExport("csv")}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        Download as CSV
                      </button>
                      <button
                        onClick={() => handleExport("excel")}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        Download as Excel
                      </button>
                      <button
                        onClick={() => handleExport("pdf")}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        Download as PDF
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Tables Section */}
      {showTable && (
        <div className="space-y-8">
          {/* If no data */}
          {cases.length === 0 && (
            <div className="flex flex-col items-center justify-center space-y-3">
              <FileText className="h-12 w-12 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-600 mb-0">
                No Data Available
              </h3>
              <p className="text-xs text-gray-400">
                Try adjusting your filters to see more results
              </p>
            </div>
          )}

          {/* Miscellaneous Arguments */}
          {miscellaneousData.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
                  Miscellaneous Arguments
                </h3>
              </div>
             <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        S.No.
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        CNR / Case Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Petitioner
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Defendant
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {miscellaneousData.map((item,index) => (
                      <tr key={item._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                         {index + 1}
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {item.cnr || item.caseNumber}
                            </div>
                            <div className="text-sm text-gray-500">
                              {item.caseType}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {item.petitioner}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {item.respondent}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Prosecution Evidence */}
          {prosecutionEvidenceData.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-red-50">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2 text-red-600" />
                  Prosecution Evidence
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        S.No.
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        CNR
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Case Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Petitioner
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {prosecutionEvidenceData.map((item,index) => (
                      <tr key={item._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {index + 1}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {item.cnr || item.caseNumber}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {item.caseType}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {item.petitioner}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {argumentsData.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-yellow-50">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Gavel className="w-5 h-5 mr-2 text-yellow-600" />
                  Arguments
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        S.No.
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        CNR / Case Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Case Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Petitioner
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Respondent
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {argumentsData.map((item,index) => (
                      <tr key={item._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {index + 1}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {item.cnr || item.caseNumber}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {item.caseType}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {item.petitioner}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {item.respondent}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Defendant Evidence */}
          {defendantEvidenceData.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-blue-50">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-blue-600" />
                  Defendant Evidence
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        S.No.
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        CNR
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Case Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Respondent
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {defendantEvidenceData.map((item) => (
                      <tr key={item._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.sNo}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {item.cnr || item.caseNumber}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {item.caseType}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {item.respondent}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Appearances */}
          {appearancesData.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-green-50">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Users className="w-5 h-5 mr-2 text-green-600" />
                  Appearances
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        S.No.
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        CNR
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Petitioner
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {appearancesData.map((item) => (
                      <tr key={item._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.sNo}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {item.cnr || item.caseNumber}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {item.petitioner}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-1 text-gray-400" />
                           {formatDisplayDate(item.hearingDate)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Final Decision */}
          {finalDecisionData.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-purple-50">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2 text-purple-600" />
                  Final Decision
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        S.No.
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        CNR
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Decision
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Remarks
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {finalDecisionData.map((item) => (
                      <tr key={item._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.sNo}
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                            {item.cnr || item.caseNumber}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {item.caseStage}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 max-w-md">
                          -
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DailyListReport;
